import { useEffect, useState } from "react";

interface IHeightObserverReturn {
	bannerHeight: number;
	categoryListHeight: number;
}

const updateHeightsFn = (ref: React.RefObject<HTMLDivElement>) => {
	const fn = (
		setBannerHeight: (height: number) => void,
		setCategoryListHeight: (height: number) => void
	) => {
		if (ref.current) {
			const bannerElement = ref.current.querySelector(".Banner");
			const categoryListElement = ref.current.querySelector(".CategoryListContainer");

			if (bannerElement) {
				setBannerHeight(bannerElement.getBoundingClientRect().height);
			}
			if (categoryListElement) {
				setCategoryListHeight(categoryListElement.getBoundingClientRect().height);
			}
		}
	};

	return fn;
};

export const useHeightObserver = (
	listRef: React.RefObject<HTMLDivElement>
): IHeightObserverReturn => {
	const [bannerHeight, setBannerHeight] = useState(0);
	const [categoryListHeight, setCategoryListHeight] = useState(0);

	useEffect(() => {
		const updateHeights = updateHeightsFn(listRef);
		const updateHeightsWithSetters = () => updateHeights(setBannerHeight, setCategoryListHeight);

		// Delay initial calculation to ensure elements are rendered
		const timeoutId = setTimeout(updateHeightsWithSetters, 0);

		// Use ResizeObserver to monitor element size changes (more precise)
		let resizeObserver: ResizeObserver | null = null;

		if (listRef.current) {
			resizeObserver = new ResizeObserver(() => {
				updateHeightsWithSetters();
			});

			const bannerElement = listRef.current.querySelector(".Banner");
			const categoryListElement = listRef.current.querySelector(".CategoryListContainer");

			if (bannerElement) resizeObserver.observe(bannerElement);
			if (categoryListElement) resizeObserver.observe(categoryListElement);
		}

		// Monitor window resize as fallback
		let resizeTimeoutId: number;
		const debouncedUpdateHeights = () => {
			clearTimeout(resizeTimeoutId);
			resizeTimeoutId = globalThis.setTimeout(updateHeightsWithSetters, 100);
		};

		window.addEventListener("resize", debouncedUpdateHeights);

		return () => {
			clearTimeout(timeoutId);
			clearTimeout(resizeTimeoutId);
			window.removeEventListener("resize", debouncedUpdateHeights);
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, [listRef]);

	return { bannerHeight, categoryListHeight };
};
